import { isAxiosError } from "axios";

export type SubmitPromptOptions = {
  openPanel?: boolean;
};

export const shouldOpenPanelOnSubmit = (options?: SubmitPromptOptions): boolean => options?.openPanel !== false;

/**
 * Whether the server confirmed that a stored conversation no longer exists.
 * Only that answer may forget it; a transport fault or a server error has to
 * leave the conversation selected so it can be read again later.
 */
export const isMissingConversationError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

/**
 * Whether a stored transcript may be restored yet.
 *
 * A cached transcript is handed back while it is being read again, and
 * restoring that one ends the wait for the read. Ending the wait unsubscribes
 * from the request, which cancels it, so the turns added after the cache was
 * written would stay missing. Nothing is restored while a read is in flight.
 */
export const canRestoreStoredTranscript = <T>(
  storedTranscript: T | undefined,
  isReadingStoredTranscript: boolean,
): storedTranscript is T => storedTranscript !== undefined && !isReadingStoredTranscript;

/**
 * Whether Pluto is in the middle of something a new prompt would disturb.
 *
 * A prompt sent while the stored transcript is still arriving would replace it,
 * so restoring counts as being busy just as answering does.
 */
export const isPlutoBusy = (state: {
  readonly isAwaitingReply: boolean;
  readonly pendingHydrationConversationId: string | null;
}): boolean => state.isAwaitingReply || state.pendingHydrationConversationId !== null;

/**
 * Whether the work in progress is work the reader can stop.
 *
 * Everything Pluto does now happens inside one streamed turn, drafting
 * included, so anything worth waiting for is also something Stop can cancel.
 */
export const canStopPlutoTurn = (state: { readonly isAwaitingReply: boolean }): boolean => state.isAwaitingReply;

/** A question asked while Pluto was busy, waiting for its turn on the wire. */
export type PlutoWaitingPrompt = {
  readonly id: string;
  readonly text: string;
};

/**
 * How many questions may wait.
 *
 * A waiting question is answered against a transcript the reader has not seen
 * yet, so the further back in the queue it sits the less it is likely to still
 * be what they wanted to ask. A short queue keeps the follow-up useful and
 * keeps a held-down Enter key from booking an afternoon of model calls.
 */
export const PLUTO_WAITING_PROMPT_LIMIT = 3;

export const canEnqueuePlutoPrompt = (waiting: readonly PlutoWaitingPrompt[]): boolean =>
  waiting.length < PLUTO_WAITING_PROMPT_LIMIT;

/**
 * Whether a waiting question should be sent now.
 *
 * Only when nothing else occupies Pluto, which keeps the one-question-at-a-time
 * rule the transcript depends on: each turn is still built from a history that
 * includes every answer before it. A failed turn holds the queue, because its
 * error is reported beside the transcript rather than inside it, so sending the
 * next question would clear the only account of what went wrong.
 */
export const shouldReleaseWaitingPrompt = (state: {
  readonly isBusy: boolean;
  readonly replyError: string | null;
  readonly waitingPrompts: readonly PlutoWaitingPrompt[];
}): boolean => !state.isBusy && state.replyError === null && state.waitingPrompts.length > 0;
